<script lang="ts" setup>
import { AbcIcon } from "@ordbogen-npm/abc-dsk";
import { computed, readonly, toRefs } from "vue";
import { useI18n } from "vue-i18n";

import { Gradient, Size } from "@/abc-dsk/options";
import { mapColorToGradient } from "@/abc-dsk/utils";
import { unpackAchievementBadge } from "@/common/composables/use-vocab-achievements";
import { type Achievement, AchievementType, type UnpackedBadge } from "@/common/types/vocab";

import AchievementItemIcon from "./AchievementItemIcon.vue";
import AchievementItemProgressBar from "./AchievementItemProgressBar.vue";

const props = defineProps<{
  achievement: Achievement;
  showStepsInName: boolean;
}>();

const { t } = useI18n();

const { title, color, progress, steps, type } = readonly(toRefs(props.achievement));

const achievedAt = computed<Date | null>(() =>
  props.achievement.achievedAt ? new Date(props.achievement.achievedAt) : null
);

const badge = computed<UnpackedBadge | null>(() => {
  if (!props.achievement.badge) {
    return null;
  }

  return unpackAchievementBadge(props.achievement);
});

const achievementDisplayName = computed<string>(() => {
  if (props.showStepsInName) {
    return `${title} ${steps}`;
  }

  return title;
});

const isUnopened = computed(() => achievedAt.value && !badge.value?.openedAt);

const isCampaign = computed<boolean>(() => type === AchievementType.Campaign);
const progressBarGradient = computed<Gradient | undefined>(() => {
  if (!isCampaign.value) {
    return undefined;
  }

  return isUnopened.value ? mapColorToGradient(color) : undefined;
});
</script>

<template>
  <article class="achievement-simple-item">
    <AchievementItemIcon :size="Size.Sm" :color="color" :notification="isUnopened ? true : false" />
    <div class="achievement-simple-item__container">
      <div class="achievement-simple-item__top-container">
        <h2
          class="achievement-simple-item__title"
          :class="`achievement-simple-item__title--${color}`"
          :title="achievementDisplayName"
          lang="en"
        >
          {{ achievementDisplayName }}
        </h2>
        <AbcIcon
          v-if="isUnopened"
          :aria-label="t('vocab.achievements.simpleItem.unopenedIconLabel')"
          name="check-mark"
          :color="color"
          width="24"
          height="24"
          class="achievement-simple-item__checkmark-icon"
        />
      </div>
      <AchievementItemProgressBar
        :color="color"
        :gradient="progressBarGradient"
        :steps="steps"
        :progress="progress"
        progress-position="right"
      />
    </div>
  </article>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.achievement-simple-item {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  padding: 20px;
  background: @colors[@white];
  gap: 10px;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  &__top-container {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    margin-bottom: 0;
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    text-overflow: ellipsis;
    white-space: nowrap;

    each(@theme-colors, {
      @name: replace(@key, "@", "");

      &--@{name} {
        color: rgb(@value[base]);
      }
    });
  }

  &__checkmark-icon {
    min-width: 24px;
    min-height: 24px;
  }
}
</style>
